/**
 * D-EZ-104 / D-EZ-105 — STAKES BEFORE THE DICE, AND THE COMMISSIONER'S THROWS.
 *
 * Howard: "I like ALL of that except that which I affect. I dont want to have a
 * decision I make affect the game or league. Its all dice driven."
 *
 * So this is presentation and nothing else. The race is already resolved; the
 * table replays its recorded dice. What this module adds is the moment BEFORE a
 * pivotal throw — what the roll needs (from the printed rule and the cards on
 * the board) and what rides on it (the lead, the playoff cut, a winless run, a
 * feud) — and one line after it lands saying what it meant.
 *
 * ===========================================================================
 * IT NEVER LOOKS AT THE THROW IT IS ABOUT TO SHOW. This is the whole design.
 * ===========================================================================
 *
 * A stakes card for throw N of a turn is built from:
 *
 *   • the board as it stood before the turn (what is on screen),
 *   • the throws of this turn BEFORE N, which the table has already revealed
 *     (a race cam call, an earlier challenge's swap, the black die that picked
 *     a pit-road challenger, the photo-finish decider),
 *   • throw N's TYPE and the drivers the printed procedure has already put in
 *     it (the trailing TOP-group holder of the called quality; the leader),
 *   • the league's history up to the previous race (`StakesContext`).
 *
 * It never reads throw N's faces, its outcome, or anything after it. Where the
 * engine's record is the only place a pre-throw fact is written down (who the
 * k-th challenger is), only that fact is read — never the defender, the swap
 * or the chips spent, which ARE outcomes. `stakes.test.ts` proves it twice:
 * structurally (doctor the recorded faces and outcomes of throw N and later,
 * the card is byte-identical) and by counterfactual (re-run the race on the
 * same dice up to throw N and different dice from N on — every card for throw N
 * is identical in both races).
 *
 * The after-line (`afterLine`) is the one place an outcome is read, and the
 * table only calls it once the throw has landed.
 */

import type { RaceResult } from '../../domain/race.ts';
import type { Car, Group, Quality, RaceState } from '../../domain/types.ts';
import { groupOf } from '../../domain/types.ts';
import type { TurnResult } from '../../domain/turn.ts';
import { RAIN_OFFICIAL_FRACTION } from '../../domain/turn.ts';
import type { DrawNotation, ObservedDraw } from '../../domain/dice.ts';
import { drawsAtFinish, drawsForTurn } from '../../domain/diceTrace.ts';
import type { ChallengeResult, ChallengeSequenceResult } from '../../domain/challenge.ts';
import { TOP_BOX_POSITION, brakePedal, type BrakeCalc } from '../../domain/challenge.ts';
import { CLEAN_AIR_BRAKE_BONUS } from '../../domain/houseRules.ts';
import type { TroubleResult } from '../../domain/trouble.ts';
import { intensityFor, trackTypeOf } from '../../domain/troubleMatrix.ts';
import { adjustmentValue } from '../../domain/repair.ts';
import { isSuperSpeedway } from '../../domain/drafting.ts';
import type { PfaAttempt } from '../../domain/photoFinish.ts';
import type { BoothSeasonContext } from '../../domain/booth/context.ts';
import { plainLine } from '../../components/story/plain.ts';
import { raceViewAt } from '../raceView.ts';
import { planCommissionerStep, revealCamCall, visibleTurnState, type Peek } from '../bigMoments.ts';
import {
  RARE_BAND_WORDS,
  atMostWords,
  challengeFace,
  challengeOdds,
  chance,
  dieAtMost,
  escapeOdds,
  inN,
  pct,
  pfaOdds,
  pitLeadOdds,
  rainOdds,
  rareBands,
  sumAtMostWords,
  type Chance,
} from './odds.ts';

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/**
 * What the league and the story layers know going in. Everything optional: an
 * exhibition has none of it and still gets the odds and the race facts.
 *
 * `pairNote` and `driverNote` are the seams for D-EZ-107 (personalities) and
 * D-EZ-108 (the rivalry heat engine): feed them later and their lines compete
 * with the history lines for the "what rides on it" slots. They must answer
 * from history up to the previous race only.
 */
export interface StakesContext {
  /** The booth's season memory — `seasonContextBefore(...)`, history up to the previous race. */
  season?: BoothSeasonContext | null;
  /** A short line about two drivers' history (rivalry heat), or null. */
  pairNote?: (a: string, b: string) => string | null;
  /** A short line about one driver (personality, backstory), or null. */
  driverNote?: (driverId: string) => string | null;
}

/** The race rules the presentation needs to price a roll. From the race's own options. */
export interface StakesRules {
  /** HOUSE RULE D-EZ-86 in force. */
  cleanAir: boolean;
  /** p.xiv solitaire chip auto-spend. */
  useChips: boolean;
  /** p.xiv — the leader spends a chip to keep the lead on a pit-road challenge. */
  leaderSpendsChip: boolean;
  /** EZRA Trouble Matrix (Chart C) in force. */
  troubleMatrix: boolean;
}

export const DEFAULT_STAKES_RULES: StakesRules = { cleanAir: true, useChips: true, leaderSpendsChip: true, troubleMatrix: true };

/** Read the rules off a race's options (`RaceOptions`-shaped; every field optional). */
export function stakesRulesFrom(options?: {
  cleanAir?: false;
  useChips?: boolean;
  leaderSpendsChip?: boolean;
  troubleMatrix?: false;
} | null): StakesRules {
  return {
    cleanAir: options?.cleanAir !== false,
    useChips: options?.useChips ?? true,
    leaderSpendsChip: options?.leaderSpendsChip ?? true,
    troubleMatrix: options?.troubleMatrix !== false,
  };
}

export interface RaceFrame {
  result: RaceResult;
  /** The grid before turn one. */
  start: RaceState;
  rules: StakesRules;
}

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

export type StakesKind = 'lead-challenge' | 'top-challenge' | 'pit-lead' | 'trouble' | 'rare' | 'rain' | 'photo-finish';

export interface StakesLine {
  text: string;
  /** "67%" — the chance this line is about. */
  odds?: string;
}

export interface StakesCard {
  kind: StakesKind;
  /** "Turn 46 of 50 · 5 to go". */
  when: string;
  /** "Ballew goes for the lead". */
  title: string;
  /** The bottom line: "Ballew takes the lead" — "33%". */
  headline: StakesLine | null;
  /** What the roll needs. */
  needs: StakesLine[];
  /** What rides on it: one or two short lines. */
  rides: string[];
  /** Rule references, shown only with "Show rulebook citations". */
  cite: string;
  /** Drivers in it, to light on the board. */
  drivers: string[];
}

/** Where, after the throw, to read what happened. An address, never an outcome. */
export type MomentRef =
  | { kind: 'lead-challenge'; seq: number; k: number; challengerId: string; leaderId: string; tieWin: string | null; tieHold: string | null }
  | { kind: 'top-challenge'; seq: number; k: number; challengerId: string; tieWin: string | null }
  | { kind: 'pit-lead'; challengerId: string; leaderId: string; tieWin: string | null }
  | { kind: 'trouble' | 'rare'; group: Group }
  | { kind: 'rain'; leaderId: string | null }
  | { kind: 'photo-finish'; attempt: number };

export interface TableMoment {
  key: string;
  /** The cue the commissioner throws (index into `rollsForTurn(turn, finish)`). */
  start: number;
  /** Exclusive: the cues revealed with this throw. */
  end: number;
  card: StakesCard;
  /**
   * TWO BEATS (a challenge whose WHITE die picks the defender). The card the
   * table shows once the white die has landed, for each face it can show — all
   * six built before the throw, so none of them can know which one lands. The
   * table throws the white die first, shows `beats[white]`, then red and blue.
   */
  beats?: Partial<Record<number, StakesCard>>;
  ref: MomentRef;
  /** Why it stopped: 'late', 'restart', 'story', or the kind's own reason. */
  why: string;
}

export interface TurnPlan {
  turn: number;
  moments: TableMoment[];
}

// ---------------------------------------------------------------------------
// The throws of a turn, by TYPE only
// ---------------------------------------------------------------------------

export interface ThrowSlot {
  phase: string;
  notation: DrawNotation;
  sides: number;
  roles: string[];
  finish: boolean;
}

/**
 * The turn's throws in the SAME order and grouping as `rollsForTurn` (the dice
 * table's cues), carrying only what kind of throw each one is. No faces.
 */
export function throwSlots(turn: TurnResult | undefined, finish: readonly ObservedDraw[] = []): ThrowSlot[] {
  if (!turn) return [];
  const own = drawsForTurn(turn) ?? [];
  const tail = new Set(finish);
  const draws = [...own, ...finish];
  const out: ThrowSlot[] = [];
  let last: number | null = null;
  for (const d of draws) {
    if (last === d.throwIndex) {
      out[out.length - 1].roles.push(d.role ?? '');
      continue;
    }
    last = d.throwIndex;
    out.push({
      phase: (d.phase ?? '').replace(/^turn \d+ — /, ''),
      notation: d.notation,
      sides: d.sides,
      roles: [d.role ?? ''],
      finish: tail.has(d),
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/** The board before turn `t` (1-based) — what is on screen when it begins. */
export function boardBefore(frame: RaceFrame, t: number): RaceState {
  return t <= 1 ? frame.start : frame.result.turns[t - 2]?.state ?? frame.start;
}

const ORD = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};

const sorted = (state: RaceState): Car[] => [...state.grid].sort((a, b) => a.position - b.position);

function whenWords(t: number, scheduled: number, prefix = ''): string {
  const head = prefix ? `${prefix} · ` : '';
  if (t > scheduled) return `${head}Extra turn ${t} — it cannot finish under yellow`;
  if (t === scheduled) return `${head}Final turn · ${t} of ${scheduled}`;
  return `${head}Turn ${t} of ${scheduled} · ${scheduled - t + 1} to go`;
}

/** The last third of a race, where the table stops for any lead fight. */
export const LATE_FRACTION = 0.8;
/** Restarts and story stops only count from here. */
export const STORY_FROM_FRACTION = 0.3;
/** A hook at least this heavy can stop the table on its own. */
export const STRONG_HOOK = 5;
/** A photo-finish attempt for this position or better stops the table. */
export const PFA_STOP_POSITION = 5;
/** Trouble in the MIDDLE group always stops the table from this fraction on (before it, on pace). */
export const MIDDLE_TROUBLE_FROM = 0.8;

const isLate = (t: number, scheduled: number): boolean => t > scheduled * LATE_FRACTION;

// ---------------------------------------------------------------------------
// What rides on it — history up to the previous race, and the board
// ---------------------------------------------------------------------------

export interface Hook {
  text: string;
  weight: number;
  /** Short form to tie into the after-line, when it reads naturally there. */
  tie?: string;
}

function seasonPhrase(ctx: BoothSeasonContext | null | undefined, seasonNumber: number): string {
  if (!ctx) return `in season ${seasonNumber}`;
  if (seasonNumber === ctx.seasonNumber - 1) return 'last season';
  if (seasonNumber === ctx.seasonNumber) return 'earlier this season';
  return `in season ${seasonNumber}`;
}

/** Hooks about one driver. `board` is the board before the turn. */
export function driverHooks(id: string, board: RaceState, frame: RaceFrame, ctx: StakesContext | undefined, role: 'challenger' | 'leader' | 'any'): Hook[] {
  const out: Hook[] = [];
  const s = ctx?.season;
  const car = board.grid.find((c) => c.card.driverId === id);
  const name = car?.card.name ?? id;
  if (s) {
    for (const m of s.milestonesInPlay.filter((x) => x.driverId === id)) {
      if (m.kind === 'first-win' && m.n >= 10) out.push({ text: `${name} is still looking for his first win, ${m.n} starts in.`, weight: m.n >= 30 ? 6 : 5, tie: `winless in ${m.n} starts` });
      else if (m.kind === 'drought') out.push({ text: `${name} has not won in ${m.n} starts.`, weight: 5, tie: `${m.n} starts since his last win` });
      else if (m.kind === 'win-streak') out.push({ text: `${name} has won ${m.n} in a row.`, weight: 4, tie: `going for ${m.n + 1} straight` });
      else if (m.kind === 'nth-win') out.push({ text: `A win today would be the ${ORD(m.n)} of ${name}'s career.`, weight: 3 });
    }
    if (s.defendingRaceWinner?.driverId === id) out.push({ text: `${name} won this race ${seasonPhrase(s, s.defendingRaceWinner.seasonNumber)}.`, weight: 3, tie: 'the defending winner here' });
    const tw = s.trackWinners.find((w) => w.driverId === id);
    if (tw && tw.wins >= 2) out.push({ text: `${name} has ${tw.wins} wins at this track.`, weight: 2 });
    if (s.defendingChampion?.driverId === id) out.push({ text: `${name} is the defending champion.`, weight: 3, tie: 'the champion' });
    if (s.pointsLeader?.driverId === id && s.raceNumber > 3) {
      out.push({
        text: s.pointsLeader.marginToSecond > 0 ? `${name} leads the championship by ${s.pointsLeader.marginToSecond} points.` : `${name} is tied for the championship lead.`,
        weight: 3,
        tie: 'the points leader',
      });
    }
    const p = s.playoff;
    const b = p?.bubble.find((x) => x.driverId === id);
    if (p && b && p.racesToCut <= 10) {
      const side = b.gap >= 0 ? `${b.gap} points inside` : `${-b.gap} points outside`;
      out.push({
        text: `${name} is ${side} the playoff cut with ${p.racesToCut} race${p.racesToCut === 1 ? '' : 's'} to go.`,
        weight: p.racesToCut <= 4 ? 5 : 4,
        tie: b.gap >= 0 ? 'protecting his playoff spot' : 'chasing the playoff cut',
      });
    }
    const streak = s.activeStreaks.find((x) => x.driverId === id && (x.kind === 'top5' || x.kind === 'top10'));
    if (streak) out.push({ text: `${name} has ${streak.length} straight top-${streak.kind === 'top5' ? 'five' : 'ten'} finishes.`, weight: 2 });
  }
  const note = ctx?.driverNote?.(id);
  if (note) out.push({ text: note, weight: 2 });
  if (car) {
    if (role === 'challenger' && car.turnsLed === 0) out.push({ text: `${name} hasn't led all day.`, weight: 2, tie: "the first time he's led all day" });
    if (role === 'leader' && car.turnsLed >= 10) out.push({ text: `${name} has led ${car.turnsLed} turns today.`, weight: 2, tie: `${car.turnsLed} turns out front already` });
    const grid = frame.result.startingOrder?.indexOf(id) ?? -1;
    if (role === 'challenger' && grid >= 19) out.push({ text: `${name} started ${ORD(grid + 1)}.`, weight: 2, tie: `from ${ORD(grid + 1)} on the grid` });
  }
  return out;
}

/** Hooks about a pair: feud history, today's feud list, the rivalry engine. */
export function pairHooks(a: string, b: string, board: RaceState, ctx: StakesContext | undefined): Hook[] {
  const out: Hook[] = [];
  const name = (id: string) => board.grid.find((c) => c.card.driverId === id)?.card.name ?? id;
  const note = ctx?.pairNote?.(a, b);
  if (note) out.push({ text: note, weight: 6, tie: `and ${name(b)} will remember it` });
  const r = ctx?.season?.rivalries.find((x) => (x.a === a && x.b === b) || (x.a === b && x.b === a));
  if (r) {
    const last = r.seasons[r.seasons.length - 1];
    out.push({
      text: `${name(a)} and ${name(b)} have feuded ${r.feuds === 1 ? 'before' : `${r.feuds} times`}${last ? `, ${seasonPhrase(ctx?.season, last)} most recently` : ''}.`,
      weight: 5,
      tie: `past his old rival`,
    });
  }
  if (board.feuds.some((f) => (f.a === a && f.b === b) || (f.a === b && f.b === a))) {
    out.push({ text: `They are on the feud list today.`, weight: 5, tie: 'with the feud still burning' });
  }
  return out;
}

const pick = (hooks: Hook[], n = 2): Hook[] => {
  const seen = new Set<string>();
  return [...hooks]
    .sort((x, y) => y.weight - x.weight)
    .filter((h) => (seen.has(h.text) ? false : (seen.add(h.text), true)))
    .slice(0, n);
};

// ---------------------------------------------------------------------------
// Cards, one builder per kind. Inputs are pre-throw facts only.
// ---------------------------------------------------------------------------

export interface LeadChallengeSetting {
  turn: number;
  scheduled: number;
  restart: boolean;
  quality: Quality | 'TV';
  attempt: 1 | 2;
  challenger: Car;
  challengerChips: number;
  leader: Car;
  leaderChips: number;
  /** The board as it stands before this throw (position order). */
  order: Car[];
  chipsOf: (id: string) => number;
  cleanAir: number;
  useChips: boolean;
  autoWin: boolean;
}

const QUALITY_WORDS: Record<string, string> = { TV: 'TV' };

function brakeWords(odds: ReturnType<typeof challengeOdds>, quality: string): string {
  const v = odds.versus;
  if (!v) return '';
  const parts = [`brake ${v.brake.base}`];
  for (const b of v.brake.bonuses) {
    const n = typeof b.value === 'number' ? b.value : Number(b.value);
    if (b.name === 'defender-is-race-leader') parts.push(`+${n} leading`);
    else if (b.name.startsWith('defender-holds-called-quality')) parts.push(`+${n} he has ${QUALITY_WORDS[quality] ?? quality} too`);
    else if (b.name === 'clean-air-restart') parts.push(`+${n} clean air`);
  }
  return parts.join(', ') + (v.brake.capped ? ' — capped at 5' : '');
}

export function leadChallengeCard(s: LeadChallengeSetting, frame: RaceFrame, board: RaceState, ctx?: StakesContext): { card: StakesCard; hooks: Hook[]; tieWin: string | null; tieHold: string | null; win: Chance } {
  const C = s.challenger.card.name;
  const L = s.leader.card.name;
  const odds = challengeOdds({
    challenger: s.challenger,
    challengerChips: s.challengerChips,
    quality: s.quality,
    challengedGroup: 'leader',
    carAt: (p) => s.order[p - 1],
    chipsOf: s.chipsOf,
    cleanAir: s.cleanAir,
    useChips: s.useChips,
    autoWin: s.autoWin,
  });
  const needs: StakesLine[] = [];
  needs.push({ text: `${C} needs ${atMostWords(s.challenger.card.gasPedal, 'red die')} to get a run.`, odds: pct(odds.gas) });
  if (odds.versus) {
    if (s.leader.cannotRace) needs.push({ text: `${L} is running wounded and cannot defend.` });
    else {
      const hold = chance(odds.versus.beatBrake.of - odds.versus.beatBrake.hits, odds.versus.beatBrake.of);
      needs.push({ text: `${L} shuts the door with ${atMostWords(odds.versus.brake.effective, 'blue die')} (${brakeWords(odds, s.quality)}).`, odds: pct(hold) });
    }
  }
  const c = s.useChips ? s.challengerChips : 0;
  const d = s.useChips && !s.leader.cannotRace ? s.leaderChips : 0;
  if (s.autoWin) needs.push({ text: `${C}'s interview bonus wins this one outright, whatever the dice say.` });
  else if (c > 0 && d > 0 && c === d) needs.push({ text: `Both have a performance chip. They cancel out — the dice decide.` });
  else if (c > 0 && d > 0 && c > d) needs.push({ text: `${C}'s ${c} chips beat ${L}'s ${d}: he controls this one either way.` });
  else if (c > 0 && d > 0 && d > c) needs.push({ text: `${L}'s ${d} chips beat ${C}'s ${c}: he controls this one either way.` });
  else if (c > 0) needs.push({ text: `${C} has a performance chip: if the dice say no, he burns it and goes anyway.` });
  else if (d > 0) needs.push({ text: `${L} has a performance chip: if ${C} gets by, he burns it and keeps the lead.` });
  if (s.challenger.card.triangle) needs.push({ text: `A red 6 breaks ${C}'s car — the triangle on his card.` });

  const hooks = pick([
    ...pairHooks(s.challenger.card.driverId, s.leader.card.driverId, board, ctx),
    ...driverHooks(s.challenger.card.driverId, board, frame, ctx, 'challenger'),
    ...driverHooks(s.leader.card.driverId, board, frame, ctx, 'leader'),
  ]);
  const cHooks = pick([
    ...pairHooks(s.challenger.card.driverId, s.leader.card.driverId, board, ctx),
    ...driverHooks(s.challenger.card.driverId, board, frame, ctx, 'challenger'),
  ], 1);
  const lHooks = pick(driverHooks(s.leader.card.driverId, board, frame, ctx, 'leader'), 1);
  const late = isLate(s.turn, s.scheduled);
  const title = s.attempt === 2 ? `${C} goes again — for the lead` : s.restart ? `Restart: ${C} goes for the lead` : `${C} goes for the lead`;
  return {
    card: {
      kind: 'lead-challenge',
      when: whenWords(s.turn, s.scheduled, s.restart ? 'Restart' : ''),
      title,
      headline: { text: `${C} takes the lead`, odds: pct(odds.win) },
      needs,
      rides: hooks.length ? hooks.map((h) => h.text) : [late ? `${L} leads with ${Math.max(1, s.scheduled - s.turn + 1)} to go.` : `${L} has the lead; ${C} has ${QUALITY_WORDS[s.quality] ?? s.quality} and the run.`],
      cite: 'RB/vi-vii quality challenge · RB/xiv performance chips' + (s.cleanAir ? ' · D-EZ-86 clean air' : ''),
      drivers: [s.challenger.card.driverId, s.leader.card.driverId],
    },
    hooks,
    tieWin: cHooks[0]?.tie ?? null,
    tieHold: lHooks[0]?.tie ?? null,
    win: odds.win,
  };
}

const TOP_BOXES = [1, 2, 3, 4, 5, 6] as const;

function brakeLadder(brake: BrakeCalc, quality: string): string {
  const parts = [`brake ${brake.base}`];
  for (const b of brake.bonuses) {
    const n = typeof b.value === 'number' ? b.value : Number(b.value);
    if (b.name === 'defender-in-top-group') parts.push(`+${n} in the top seven`);
    else if (b.name.startsWith('defender-holds-called-quality')) parts.push(`+${n} he has ${QUALITY_WORDS[quality] ?? quality} too`);
    else if (b.name === 'clean-air-restart') parts.push(`+${n} clean air`);
  }
  return parts.join(', ') + (brake.capped ? ' — capped at 5' : '');
}

/** "M. Cutshaw" — short, and two Ackerds in the top seven stay two people. */
const surname = (car: Car): string => {
  const parts = car.card.name.split(' ');
  return parts.length > 1 ? `${parts[0][0]}. ${parts.slice(1).join(' ')}` : car.card.name;
};

export interface TopChallengeBuilt {
  card: StakesCard;
  /** The second beat, by white face. */
  beats: Record<number, StakesCard>;
  hooks: Hook[];
  tieWin: string | null;
  win: Chance;
}

/**
 * A challenge INTO THE TOP GROUP (from the MIDDLE): the WHITE die picks which of
 * the six cars at P2-P7 he goes after (Board A, one car per box), so the
 * defender is not known until it lands. Beat one says what he needs and who the
 * white die can pick; `beats[w]` is the card once white shows `w` — every one
 * of the six built here, before the throw.
 */
export function topChallengeCard(s: ChallengeSetting, frame: RaceFrame, board: RaceState, ctx?: StakesContext): TopChallengeBuilt {
  const C = s.challenger.card.name;
  const from = s.order.findIndex((x) => x.card.driverId === s.challenger.card.driverId) + 1;
  const input = {
    challenger: s.challenger,
    challengerChips: s.challengerChips,
    quality: s.quality,
    challengedGroup: 'top' as const,
    carAt: (p: number) => s.order[p - 1],
    chipsOf: s.chipsOf,
    cleanAir: s.cleanAir,
    useChips: s.useChips,
    autoWin: s.autoWin,
  };
  const odds = challengeOdds(input);
  const late = s.turn > s.scheduled - 5;
  const cHooks = driverHooks(s.challenger.card.driverId, board, frame, ctx, 'challenger');
  const hooks = pick(cHooks);
  const when = whenWords(s.turn, s.scheduled, s.restart ? 'Restart' : '');
  const boxes = TOP_BOXES.map((w) => ({ w, pos: TOP_BOX_POSITION[w], car: s.order[TOP_BOX_POSITION[w] - 1] as Car | undefined }));
  const c = s.useChips ? s.challengerChips : 0;
  const needs: StakesLine[] = [
    { text: `${C} needs ${atMostWords(s.challenger.card.gasPedal, 'red die')} to get a run.`, odds: pct(odds.gas) },
    { text: `The white die picks his man: ${boxes.map((b) => `${b.w} ${b.car ? surname(b.car) : 'empty'}`).join(' · ')}.` },
  ];
  if (s.autoWin) needs.push({ text: `${C}'s interview bonus wins this one outright, whatever the dice say.` });
  else if (c > 0) needs.push({ text: `${C} has a performance chip in hand.` });
  if (s.challenger.card.triangle) needs.push({ text: `A red 6 breaks ${C}'s car — the triangle on his card.` });
  const card: StakesCard = {
    kind: 'top-challenge',
    when,
    title: `${C}, ${ORD(from)}, takes a run at the top seven`,
    headline: { text: `${C} gets by`, odds: pct(odds.win) },
    needs,
    rides: hooks.length ? hooks.map((h) => h.text) : [late ? `A top-seven finish is on the line with ${Math.max(1, s.scheduled - s.turn + 1)} to go.` : `A spot in the top seven.`],
    cite: 'RB/vi-vii quality challenge (Board A, white picks the box) · RB/xiv performance chips',
    drivers: [s.challenger.card.driverId],
  };

  const beats: Record<number, StakesCard> = {};
  for (const b of boxes) {
    const D = b.car;
    if (!D) {
      beats[b.w] = { ...card, title: `White ${b.w}: P${b.pos} is empty`, headline: null, needs: [{ text: 'Nobody to challenge — the slot is empty, so nothing happens.' }], rides: [] };
      continue;
    }
    let hits = 0;
    for (let r = 1; r <= 6; r++) for (let bl = 1; bl <= 6; bl++) if (challengeFace(input, r, b.w, bl).success) hits++;
    const win = chance(hits, 36);
    const brake = brakePedal(D, b.pos, s.quality, s.cleanAir);
    const dName = D.card.name;
    const d = s.useChips && !D.cannotRace ? s.chipsOf(D.card.driverId) : 0;
    const beatNeeds: StakesLine[] = [{ text: `${C} needs ${atMostWords(s.challenger.card.gasPedal, 'red die')} to get a run.`, odds: pct(odds.gas) }];
    if (D.cannotRace) beatNeeds.push({ text: `${dName} is running wounded and cannot defend.` });
    else beatNeeds.push({ text: `${dName} shuts the door with ${atMostWords(brake.effective, 'blue die')} (${brakeLadder(brake, s.quality)}).`, odds: pct(dieAtMost(brake.effective)) });
    if (s.autoWin) beatNeeds.push({ text: `${C}'s interview bonus wins this one outright.` });
    else if (c > 0 && d > 0 && c === d) beatNeeds.push({ text: 'Both have a performance chip. They cancel out — the dice decide.' });
    else if (c > 0 && d > 0) beatNeeds.push({ text: `${c > d ? C : dName} has more chips and controls this one either way.` });
    else if (c > 0) beatNeeds.push({ text: `${C} has a performance chip: if the dice say no, he burns it and goes anyway.` });
    else if (d > 0) beatNeeds.push({ text: `${dName} has a performance chip: if ${C} gets by, he burns it and holds on.` });
    const bHooks = pick([
      ...pairHooks(s.challenger.card.driverId, D.card.driverId, board, ctx),
      ...cHooks,
      ...driverHooks(D.card.driverId, board, frame, ctx, 'any'),
    ]);
    beats[b.w] = {
      kind: 'top-challenge',
      when,
      title: `White ${b.w}: ${C} goes after ${dName} for ${ORD(b.pos)}`,
      headline: { text: `${C} takes ${ORD(b.pos)}`, odds: pct(win) },
      needs: beatNeeds,
      rides: bHooks.length ? bHooks.map((h) => h.text) : [late ? `${ORD(b.pos)} place with ${Math.max(1, s.scheduled - s.turn + 1)} to go.` : `${ORD(b.pos)} place.`],
      cite: card.cite,
      drivers: [s.challenger.card.driverId, D.card.driverId],
    };
  }
  return { card, beats, hooks, tieWin: pick(cHooks, 1)[0]?.tie ?? null, win: odds.win };
}

export interface PitLeadSetting {
  turn: number;
  scheduled: number;
  /** The last scheduled stop of the race. */
  last: boolean;
  challenger: Car;
  leader: Car;
  leaderChip: boolean;
  leaderSpendsChip: boolean;
}

export function pitLeadCard(s: PitLeadSetting, frame: RaceFrame, board: RaceState, ctx?: StakesContext): { card: StakesCard; tieWin: string | null; strong: boolean } {
  const C = s.challenger.card.name;
  const L = s.leader.card.name;
  const odds = pitLeadOdds(s.challenger.card.gasPedal, s.leaderChip, s.leaderSpendsChip);
  const needs: StakesLine[] = [{ text: `${C} needs ${atMostWords(s.challenger.card.gasPedal, 'red die')} to beat ${L} off pit road. No defence on pit road.`, odds: pct(odds.gas) }];
  if (s.leaderChip && s.leaderSpendsChip) needs.push({ text: `${L} has a performance chip and will burn it to keep the lead.` });
  const hooks = pick([
    ...pairHooks(s.challenger.card.driverId, s.leader.card.driverId, board, ctx),
    ...driverHooks(s.challenger.card.driverId, board, frame, ctx, 'challenger'),
    ...driverHooks(s.leader.card.driverId, board, frame, ctx, 'leader'),
  ]);
  return {
    card: {
      kind: 'pit-lead',
      when: whenWords(s.turn, s.scheduled, s.last ? 'Last pit stop' : 'Pit stop'),
      title: `${C} gets a shot at ${L} off pit road`,
      headline: { text: `${C} comes out in the lead`, odds: pct(odds.lead) },
      needs,
      rides: hooks.length ? hooks.map((h) => h.text) : [s.last ? `The last stop of the day — whoever leads out of the pits has the track.` : `Whoever leads out of the pits has the track.`],
      cite: 'RB/xiii-xiv(4) pit-turn lead challenge · RB/xiv leader chip',
      drivers: [s.challenger.card.driverId, s.leader.card.driverId],
    },
    tieWin: pick([...driverHooks(s.challenger.card.driverId, board, frame, ctx, 'challenger')], 1)[0]?.tie ?? null,
    strong: hooks.some((h) => h.weight >= STRONG_HOOK),
  };
}

export interface TroubleSetting {
  turn: number;
  scheduled: number;
  group: 'top' | 'middle' | 'bottom';
  kind: 'trouble' | 'rare';
  /** The group as it stands on the board before the turn (LEADER rides with TOP). */
  cars: Car[];
  trackCategory: string;
  escapeAdj: number;
  superSpeedway: boolean;
  troubleMatrix: boolean;
}

const GROUP_NAME: Record<string, string> = { top: 'TOP', middle: 'MIDDLE', bottom: 'BOTTOM' };

/** The printed escape number for one car before the incident type is known (Chart C is shown beside it). */
export function escapeTarget(car: Car, escapeAdj: number, superSpeedway: boolean): number {
  return car.card.escape + escapeAdj + (car.card.trait === 'SS' && superSpeedway ? 1 : 0);
}

export function troubleCard(s: TroubleSetting, frame: RaceFrame, board: RaceState, ctx?: StakesContext): StakesCard {
  const G = GROUP_NAME[s.group];
  const leaderId = board.grid.find((c) => c.position === 1)?.card.driverId;
  const scored = s.cars.map((c) => {
    const hooks = driverHooks(c.card.driverId, board, frame, ctx, 'any');
    return { c, hooks, weight: hooks.reduce((n, h) => Math.max(n, h.weight), 0) };
  });
  // The leader first, then the drivers with the most riding on it, then running order.
  const shown = [...scored]
    .sort((a, b) => Number(b.c.card.driverId === leaderId) - Number(a.c.card.driverId === leaderId) || b.weight - a.weight || a.c.position - b.c.position)
    .slice(0, 3);
  const spendable = (c: Car) => (groupOf(c.position) === 'bottom' ? 0 : c.chips);
  const needs: StakesLine[] = [];
  if (s.kind === 'rare') {
    needs.push({ text: `Two dice read as a code, 11 to 66, on the ${G} group's rare results chart — then the driver is found.` });
    for (const b of rareBands(s.group).slice(0, 3)) needs.push({ text: `${inN(b.chance)}: ${RARE_BAND_WORDS[b.band]}.`, odds: pct(b.chance) });
  } else {
    needs.push({
      text: s.troubleMatrix
        ? `First the kind of incident is rolled: a wreck reaches every car in the ${G} group, an accident or a crash only some. Each car it reaches rolls two dice and needs his ESCAPE number or less.`
        : `Every car in the ${G} group rolls two dice and needs his ESCAPE number or less.`,
    });
    for (const { c } of shown) {
      const target = escapeTarget(c, s.escapeAdj, s.superSpeedway);
      const odds = escapeOdds({ target, awareness: c.card.trait === 'AW', chips: spendable(c), autoEscape: c.grants?.autoEscapeOnce === true });
      const clear = chance(odds.collected.of - odds.collected.hits, odds.collected.of);
      const why = c.grants?.autoEscapeOnce ? ' — his interview bonus gets him through' : spendable(c) > 0 ? ' — a chip in hand saves him if the dice don\'t' : c.card.trait === 'AW' ? ' — and his awareness gives him a second chance' : '';
      const who = c.position === 1 ? `${c.card.name} (leader)` : `${c.card.name} (${ORD(c.position)})`;
      needs.push({ text: `${who}: ${sumAtMostWords(target)}${why}.`, odds: clear.p >= 1 ? 'safe' : `${pct(clear)} clear` });
    }
    if (s.troubleMatrix) {
      const tt = trackTypeOf(s.trackCategory);
      const shifts = (['AC', 'CR', 'WK'] as const)
        .map((t) => ({ t, v: intensityFor(tt, t).escape }))
        .filter((x) => x.v !== 0);
      if (shifts.length) {
        const words = { AC: 'an accident', CR: 'a crash', WK: 'a wreck' } as const;
        const by = new Map<number, string[]>();
        for (const x of shifts) by.set(x.v, [...(by.get(x.v) ?? []), words[x.t]]);
        const said = [...by].map(([v, ws]) => `${ws.join(' or ')} makes every number ${Math.abs(v)} ${v < 0 ? 'lower' : 'higher'}`);
        needs.push({ text: `On this track ${said.join('; ')}.` });
      }
    }
    if (s.group !== 'bottom') needs.push({ text: `${s.group === 'top' ? 'Three' : 'Six'} or more collected and it spreads to the ${s.group === 'top' ? 'MIDDLE' : 'BOTTOM'} group — the Big One.` });
  }
  const hooks = pick(shown.flatMap((x) => x.hooks));
  return {
    kind: s.kind,
    when: whenWords(s.turn, s.scheduled),
    title: s.kind === 'rare' ? `A rare result in the ${G} group` : `Trouble in the ${G} group`,
    headline: null,
    needs,
    rides: hooks.length ? hooks.map((h) => h.text) : [s.group === 'top' ? `The leaders are in it.` : `${s.cars.length} cars in the ${G} group.`],
    cite: s.kind === 'rare' ? 'RB/iv RARE RESULTS' : 'RB/v TROUBLE · RB/xiv chips · EZRA-S2 Appendix C · RB/xv the Big One',
    drivers: shown.map((x) => x.c.card.driverId),
  };
}

export function rainCard(turn: number, scheduled: number, leader: Car | null, frame: RaceFrame, board: RaceState, ctx?: StakesContext): StakesCard {
  const odds = rainOdds();
  const hooks = leader ? pick(driverHooks(leader.card.driverId, board, frame, ctx, 'leader'), 1) : [];
  return {
    kind: 'rain',
    when: `End of turn ${turn} of ${scheduled} · past halfway, so it would count`,
    title: 'Is the rain coming?',
    headline: { text: 'Rain ends the race', odds: pct(odds) },
    needs: [{ text: 'A 1 or a 6 on the die and the race is over — the order as it stands is the result.', odds: pct(odds) }],
    rides: [leader ? `${leader.card.name} would win it under the clouds.` : 'Whoever leads would win it.', ...hooks.map((h) => h.text)].slice(0, 2),
    cite: 'RARE RESULTS sheet, RAIN THREAT · D-EZ-88 half distance',
    drivers: leader ? [leader.card.driverId] : [],
  };
}

export function pfaCard(a: { attacker: Car; defender: Car; to: number; chipsHeld: number }, frame: RaceFrame, board: RaceState, ctx?: StakesContext): StakesCard {
  const A = a.attacker.card.name;
  const D = a.defender.card.name;
  const odds = pfaOdds(a.attacker.card.gasPedal, a.chipsHeld, a.defender.card.gasPedal);
  const forWhat = a.to === 1 ? 'for the win' : `for ${ORD(a.to)}`;
  const hooks = pick([
    ...pairHooks(a.attacker.card.driverId, a.defender.card.driverId, board, ctx),
    ...driverHooks(a.attacker.card.driverId, board, frame, ctx, 'challenger'),
    ...driverHooks(a.defender.card.driverId, board, frame, ctx, 'leader'),
  ]);
  return {
    kind: 'photo-finish',
    when: 'At the line · photo finish',
    title: `${A} throws it at ${D} ${forWhat}`,
    headline: { text: `${A} gets him at the stripe`, odds: pct(odds.pass) },
    needs: [
      { text: `${A} needs ${atMostWords(odds.attackTarget, 'red die')} to get alongside${a.chipsHeld >= 2 ? ' (two chips buy him one)' : ''}.`, odds: pct(odds.attack) },
      { text: `${D} holds him off with ${atMostWords(a.defender.card.gasPedal, 'red die')}.`, odds: pct(odds.hold) },
    ],
    rides: hooks.length ? hooks.map((h) => h.text) : [a.to === 1 ? 'A race won by inches, or lost by them.' : `${ORD(a.to)} place and the points that go with it.`],
    cite: 'EZRA Track Pack, PHOTO FINISH ATTEMPT · RB/p.vii gas pedal check',
    drivers: [a.attacker.card.driverId, a.defender.card.driverId],
  };
}

// ---------------------------------------------------------------------------
// Planning a turn: which cues are pivotal, and their cards
// ---------------------------------------------------------------------------

export interface PlanOptions {
  ctx?: StakesContext;
  /** Throws already stopped for in this race (counted from the turns shown). */
  stopsSoFar?: number;
}

const isSeq = (e: { eventType: string }): e is ChallengeSequenceResult => e.eventType === 'quality-challenge-sequence';

/** Apply a revealed challenge's swap and chips to the working board. */
function applyChallenge(order: Car[], chips: Map<string, number>, ch: ChallengeResult): Car[] {
  for (const d of ch.chipDeltas) chips.set(d.driverId, (chips.get(d.driverId) ?? 0) + d.delta);
  const moves = ch.positionDeltas.filter((d) => typeof d.to === 'number' && d.from !== d.to);
  if (moves.length === 0) return order;
  const next = [...order];
  for (const m of moves) {
    const car = order.find((c) => c.card.driverId === m.driverId);
    if (car && m.to) next[m.to - 1] = car;
  }
  return next.map((c, i) => ({ ...c, position: i + 1 }));
}

export interface LeadChallengeAt {
  /** The cue (throw) index. */
  slot: number;
  /** Which quality-challenge sequence of the turn, and which challenge in it. */
  seq: number;
  k: number;
  setting: LeadChallengeSetting;
}

/** Any quality challenge, with what was true before its throw (D-EZ-104). */
export interface ChallengeSetting extends Omit<LeadChallengeSetting, 'leader' | 'leaderChips'> {
  /** The group it is aimed at — fixed before the dice (attempt 2's by attempt 1, already shown). */
  challengedGroup: Group;
}

export interface ChallengeAt {
  slot: number;
  seq: number;
  k: number;
  setting: ChallengeSetting;
}

/** Every challenge of turn `t` aimed at the race LEADER. See `challengesIn`. */
export function leadChallengesIn(frame: RaceFrame, t: number, slots?: ThrowSlot[], before?: RaceState): LeadChallengeAt[] {
  const out: LeadChallengeAt[] = [];
  for (const c of challengesIn(frame, t, slots, before)) {
    if (c.setting.challengedGroup !== 'leader') continue;
    const leader = c.setting.order[0];
    if (!leader || leader.card.driverId === c.setting.challenger.card.driverId) continue;
    const { challengedGroup: _g, ...rest } = c.setting;
    out.push({ slot: c.slot, seq: c.seq, k: c.k, setting: { ...rest, leader, leaderChips: c.setting.chipsOf(leader.card.driverId) } });
  }
  return out;
}

/**
 * Every quality challenge of turn `t`, with what was true before its throw.
 * Walks the turn's challenge throws in order; after each one it carries THAT
 * throw's (now revealed) swap and chips into the board the next one sees. From
 * each challenge's own record it reads only who challenges, at which group, on
 * which attempt — facts fixed before its dice.
 */
export function challengesIn(frame: RaceFrame, t: number, slots?: ThrowSlot[], before?: RaceState): ChallengeAt[] {
  const { result } = frame;
  const turn = result.turns[t - 1];
  if (!turn) return [];
  const board = before ?? boardBefore(frame, t);
  const all = slots ?? throwSlots(turn, t === result.turnsRun ? drawsAtFinish(result) : []);
  const underYellow = board.yellowTurnsRemaining > 0;
  const prevUnderYellow = t > 1 && boardBefore(frame, t - 1).yellowTurnsRemaining > 0;
  const cleanAirTurn = !underYellow && (t === 1 || prevUnderYellow);
  const restart = cleanAirTurn && t > 1;
  const cleanAir = frame.rules.cleanAir && cleanAirTurn ? CLEAN_AIR_BRAKE_BONUS : 0;

  const seqs = turn.events.filter(isSeq);
  const seqByGroup = new Map<string, number[]>();
  seqs.forEach((q, i) => seqByGroup.set(q.call.group, [...(seqByGroup.get(q.call.group) ?? []), i]));
  const cursor = new Map<string, { at: number; k: number }>();
  let order = sorted(board);
  const chips = new Map(order.map((c) => [c.card.driverId, c.chips]));
  const grantSpent = new Set<string>();
  const out: ChallengeAt[] = [];

  all.forEach((slot, i) => {
    const m = slot.notation === 'challenge' ? /QUALITY CHALLENGES, (TOP|MIDDLE|BOTTOM) group/.exec(slot.phase) : null;
    if (!m) return;
    const group = m[1].toLowerCase();
    const list = seqByGroup.get(group) ?? [];
    const c = cursor.get(group) ?? { at: 0, k: 0 };
    if (list[c.at] !== undefined && c.k >= seqs[list[c.at]].challenges.length) {
      c.at++;
      c.k = 0;
    }
    const seqIdx = list[c.at];
    if (seqIdx === undefined) return;
    const k = c.k;
    cursor.set(group, { at: c.at, k: k + 1 });
    const seq = seqs[seqIdx];
    const ch = seq.challenges[k];
    if (!ch) return;
    if (k === 0 && seq.opening) {
      // The front of the field as it stood when this sequence began (restart
      // events, drafting and multiple-event problems can move it after the
      // board was last drawn) — written down by the engine before any of its
      // dice. Only the cars and chips; nothing about how it went.
      const front = seq.opening.map((o) => order.find((x) => x.card.driverId === o.id)).filter((x): x is Car => !!x);
      const ids = new Set(front.map((x) => x.card.driverId));
      order = [...front, ...order.filter((x) => !ids.has(x.card.driverId))].map((x, n) => ({ ...x, position: n + 1 }));
      for (const o of seq.opening) chips.set(o.id, o.chips);
    }
    // PRE-THROW FACTS ONLY: who challenges, at which group, which attempt. The
    // defender, the swap and the chips spent are this throw's outcome.
    const challengerId = ch.challengerId;
    const challenger = order.find((x) => x.card.driverId === challengerId);
    if (challenger) {
      const snapshot = order;
      const chipsNow = new Map(chips);
      out.push({
        slot: i,
        seq: seqIdx,
        k,
        setting: {
          turn: t,
          scheduled: result.scheduledTurns,
          restart,
          quality: seq.call.quality,
          attempt: ch.attempt,
          challengedGroup: ch.challengedGroup,
          challenger,
          challengerChips: chipsNow.get(challengerId) ?? 0,
          order: snapshot,
          chipsOf: (id) => chipsNow.get(id) ?? 0,
          cleanAir,
          useChips: frame.rules.useChips,
          autoWin: challenger.grants?.autoWinChallengeOnce === true && !grantSpent.has(challengerId),
        },
      });
    }
    // The throw is now revealed: carry its result into the next one's board.
    order = applyChallenge(order, chips, ch);
    if (ch.grantUsed) grantSpent.add(challengerId);
  });
  return out;
}

export interface Candidate {
  moment: Omit<TableMoment, 'why'>;
  /** Stops whatever the pace (an incident, the last stop, the line, the rain). */
  always: string | null;
  lead?: { p: number; late: boolean; restart: boolean; strong: boolean };
  /** A challenge into the TOP group (two beats): late in the race, or a driver with a story. */
  top?: { p: number; late: boolean; strong: boolean };
  /** Stops only when the race has fallen behind its pace of stops. */
  onPace?: boolean;
  /** A photo finish behind the winner: stops while the race is under PFA_CAP. */
  onCap?: boolean;
}

/**
 * THE PACE OF STOPS — how a lead fight earns a throw. No look-ahead: `stops`
 * counts the throws already stopped for in this race (all revealed), and the
 * rest is this throw's own pre-throw facts.
 *
 *   • a roll the dice cannot decide (a chip or a bonus settles it either way —
 *     under 8% or over 92% before the throw) never stops the table;
 *   • a late lead fight (last fifth) stops, until the race has had `LATE_CAP`;
 *   • a restart or a strong story hook stops while the race is under its pace
 *     of `TARGET_STOPS` spread over the distance;
 *   • any live lead fight, and MIDDLE-group trouble, stops when the race has
 *     fallen `BEHIND` that pace.
 */
export const TARGET_STOPS = 11;
export const LATE_CAP = 12;
export const BEHIND = 1;
/** A photo finish for a place behind the winner stops while the race is under this many. */
export const PFA_CAP = 14;
export const DEAD_ROLL = 0.08;
/** The last turns in which a run at the top seven stops the table (one a turn). */
export const TOP_LATE_TURNS = 5;
/** A late run at the top seven stops while the race is under this many. */
export const TOP_CAP = 15;

function topWhy(c: NonNullable<Candidate['top']>, t: number, scheduled: number, stops: number, topThisTurn: boolean): string | null {
  if (topThisTurn || c.p < DEAD_ROLL || c.p > 1 - DEAD_ROLL) return null;
  if (c.late) return stops < TOP_CAP ? 'late-top' : null;
  const pace = (TARGET_STOPS * t) / scheduled;
  if (c.strong && t > scheduled * STORY_FROM_FRACTION && stops < pace) return 'story';
  return null;
}

function leadWhy(c: NonNullable<Candidate['lead']>, t: number, scheduled: number, stops: number): string | null {
  if (c.p < DEAD_ROLL || c.p > 1 - DEAD_ROLL) return null;
  if (c.late) return stops < LATE_CAP ? 'late' : null;
  const pace = (TARGET_STOPS * t) / scheduled;
  if (t > scheduled * STORY_FROM_FRACTION && stops < pace) {
    if (c.restart) return 'restart';
    if (c.strong) return 'story';
  }
  if (t > scheduled * 0.15 && stops + BEHIND <= pace) return 'pace';
  return null;
}

/**
 * Every throw of turn `t` that COULD stop the table, with its stakes card —
 * before the pace decides which do. Exported so the tests can check every
 * card, not only the ones a given race happened to stop for.
 */
export function stakesCandidates(frame: RaceFrame, t: number, ctx?: StakesContext): Candidate[] {
  const { result } = frame;
  const turn = result.turns[t - 1];
  if (!turn) return [];
  const finish = t === result.turnsRun ? drawsAtFinish(result) : [];
  const slots = throwSlots(turn, finish);
  const board = boardBefore(frame, t);
  const scheduled = result.scheduledTurns;
  const found: Candidate[] = [];

  // ---- quality challenges aimed at the LEADER, in throw order ------------
  const runEnd = (from: number): number => {
    let e = from + 1;
    while (e < slots.length && slots[e].phase === slots[from].phase && slots[e].notation !== 'challenge') e++;
    return e;
  };
  for (const at of leadChallengesIn(frame, t, slots, board)) {
    const built = leadChallengeCard(at.setting, frame, board, ctx);
    found.push({
      moment: {
        key: `${t}-lead-${at.slot}`,
        start: at.slot,
        end: runEnd(at.slot),
        card: built.card,
        ref: {
          kind: 'lead-challenge',
          seq: at.seq,
          k: at.k,
          challengerId: at.setting.challenger.card.driverId,
          leaderId: at.setting.leader.card.driverId,
          tieWin: built.tieWin,
          tieHold: built.tieHold,
        },
      },
      always: null,
      lead: { p: built.win.p, late: isLate(t, scheduled), restart: at.setting.restart, strong: built.hooks.some((h) => h.weight >= STRONG_HOOK) },
    });
  }

  // ---- runs at the top seven (two beats: the white die picks the man) ----
  for (const at of challengesIn(frame, t, slots, board)) {
    if (at.setting.challengedGroup !== 'top') continue;
    const late = t > scheduled - TOP_LATE_TURNS;
    const built = topChallengeCard(at.setting, frame, board, ctx);
    const strong = built.hooks.some((h) => h.weight >= STRONG_HOOK);
    if (!late && !strong) continue;
    found.push({
      moment: {
        key: `${t}-top-${at.slot}`,
        start: at.slot,
        end: runEnd(at.slot),
        card: built.card,
        beats: built.beats,
        ref: { kind: 'top-challenge', seq: at.seq, k: at.k, challengerId: at.setting.challenger.card.driverId, tieWin: built.tieWin },
      },
      always: null,
      top: { p: built.win.p, late, strong },
    });
  }

  // ---- the race cam's own incidents: TROUBLE and RARE -------------------
  const cam = turn.raceCam;
  if (turn.kind === 'green' && cam && cam.group !== 'duel' && (cam.result === 'TROUBLE' || cam.result === 'RARE')) {
    const g = cam.group as 'top' | 'middle' | 'bottom';
    const stop = cam.result === 'RARE' || g === 'top' || g === 'middle';
    const camAt = slots.findIndex((s) => s.notation === 'race-cam' && s.phase.startsWith('RACE CAM'));
    if (stop && camAt >= 0 && slots[camAt + 1]?.phase.startsWith('RACE CAM')) {
      let end = camAt + 2;
      while (end < slots.length && slots[end].phase.startsWith('RACE CAM')) end++;
      const cars = sorted(board).filter((c) => {
        const cg = groupOf(c.position);
        return g === 'top' ? cg === 'top' || cg === 'leader' : cg === g;
      });
      found.push({
        moment: {
          key: `${t}-${cam.result.toLowerCase()}-${camAt + 1}`,
          start: camAt + 1,
          end,
          card: troubleCard(
            {
              turn: t,
              scheduled,
              group: g,
              kind: cam.result === 'RARE' ? 'rare' : 'trouble',
              cars,
              trackCategory: result.track.category,
              escapeAdj: adjustmentValue(result.track.escapeAdj),
              superSpeedway: isSuperSpeedway(result.track),
              troubleMatrix: frame.rules.troubleMatrix,
            },
            frame,
            board,
            ctx,
          ),
          ref: { kind: cam.result === 'RARE' ? 'rare' : 'trouble', group: g },
        },
        always: cam.result === 'RARE' ? 'rare' : g === 'top' ? 'top-group' : t > scheduled * MIDDLE_TROUBLE_FROM ? 'late-trouble' : null,
        onPace: cam.result === 'TROUBLE' && g === 'middle',
      });
    }
  }

  // ---- a pit stop: a shot at the lead off pit road ------------------------
  const lastPit = Math.max(0, ...board.pitTurns.filter((p) => p <= scheduled));
  if (turn.kind === 'pit' && turn.pit) {
    const lc = turn.pit.leadChallenge;
    const redAt = slots.findIndex((s, i) => i > 0 && s.phase.startsWith('PIT TURN') && s.notation === 'd6' && s.roles[0] === 'red' && slots[i - 1].roles[0] === 'black' && slots[i - 1].phase.startsWith('PIT TURN'));
    // Who is in it was decided by the black die, which the table shows first;
    // whether the leader holds a chip by the fast pit before it.
    if (redAt >= 0 && lc.challengerId && lc.leaderId) {
      // Cards only (name, gas pedal) — read off the board as it stood.
      const challenger = board.grid.find((c) => c.card.driverId === lc.challengerId);
      const leader = board.grid.find((c) => c.card.driverId === lc.leaderId);
      // The leader's chips as they stood before the red die (the engine's
      // receipt); an older save without it falls back to the board.
      const leaderChip = (lc.leaderChips ?? leader?.chips ?? 0) > 0;
      const odds = challenger ? pitLeadOdds(challenger.card.gasPedal, leaderChip, frame.rules.leaderSpendsChip).lead.p : 0;
      if (challenger && leader && odds > DEAD_ROLL && odds < 1 - DEAD_ROLL) {
        const built = pitLeadCard({ turn: t, scheduled, challenger, leader, leaderChip, leaderSpendsChip: frame.rules.leaderSpendsChip, last: t === lastPit }, frame, board, ctx);
        found.push({
          moment: {
            key: `${t}-pit-${redAt}`,
            start: redAt,
            end: redAt + 1,
            card: built.card,
            ref: { kind: 'pit-lead', challengerId: lc.challengerId, leaderId: lc.leaderId, tieWin: built.tieWin },
          },
          always: t === lastPit ? 'last-pit' : null,
          lead: { p: odds, late: isLate(t, scheduled), restart: false, strong: built.strong },
        });
      }
    }
  }

  // ---- the rain die, past halfway -----------------------------------------
  const rainAt = slots.findIndex((s) => s.phase.startsWith('RAIN THREAT'));
  if (rainAt >= 0 && t >= board.totalTurns * RAIN_OFFICIAL_FRACTION) {
    // The rain die is the turn's last act: everything that sets the order is
    // already on the table when it is thrown, and the die cannot move a car.
    const leader = sorted(turn.state)[0] ?? null;
    found.push({
      moment: {
        key: `${t}-rain-${rainAt}`,
        start: rainAt,
        end: rainAt + 1,
        card: rainCard(t, board.totalTurns, leader, frame, board, ctx),
        ref: { kind: 'rain', leaderId: leader?.card.driverId ?? null },
      },
      always: 'rain',
    });
  }

  // ---- the photo finish ----------------------------------------------------
  const pf = result.photoFinish;
  if (finish.length && pf && pf.attempts.length) {
    const first = slots.findIndex((s) => s.finish);
    let at = first + 1; // after the decider die
    const before = sorted(turn.state);
    pf.attempts.forEach((a, j) => {
      const start = at;
      // Where the NEXT attempt's dice start is only known once this one's are
      // on the table — playback grouping, never read into a card.
      at += a.defenceRoll === null ? 1 : 2;
      if (a.toPosition > PFA_STOP_POSITION) return;
      const attacker = before.find((c) => c.card.driverId === a.attackerId);
      const defender = before.find((c) => c.card.driverId === a.defenderId);
      if (!attacker || !defender || start >= slots.length) return;
      const odds = pfaOdds(attacker.card.gasPedal, a.chipsHeld, defender.card.gasPedal).pass.p;
      if (odds <= 0) return;
      found.push({
        moment: {
          key: `${t}-pfa-${j}`,
          start,
          end: Math.min(slots.length, at),
          card: pfaCard({ attacker, defender, to: a.toPosition, chipsHeld: a.chipsHeld }, frame, board, ctx),
          ref: { kind: 'photo-finish', attempt: j },
        },
        always: a.toPosition === 1 ? 'photo-finish' : null,
        onCap: true,
      });
    });
  }

  return found.sort((x, y) => x.moment.start - y.moment.start);
}

export function planTurn(frame: RaceFrame, t: number, opts: PlanOptions = {}): TurnPlan {
  const scheduled = frame.result.scheduledTurns;
  const found = stakesCandidates(frame, t, opts.ctx);
  // Decide in throw order: each stop counts toward the next one's pace.
  let stops = opts.stopsSoFar ?? 0;
  let topThisTurn = false;
  const moments: TableMoment[] = [];
  for (const c of found) {
    const pace = (TARGET_STOPS * t) / scheduled;
    const why =
      c.always ??
      (c.lead
        ? leadWhy(c.lead, t, scheduled, stops)
        : c.top
          ? topWhy(c.top, t, scheduled, stops, topThisTurn)
          : c.onPace && t > scheduled * 0.15 && stops + BEHIND <= pace
            ? 'pace'
            : c.onCap && stops < PFA_CAP
              ? 'photo-finish'
              : null);
    if (!why) continue;
    if (c.top) topThisTurn = true;
    stops++;
    moments.push({ ...c.moment, why });
  }
  return { turn: t, moments };
}

// ---------------------------------------------------------------------------
// After the throw — one punchy line. READS THE OUTCOME; call only once it landed.
// ---------------------------------------------------------------------------

const nameIn = (frame: RaceFrame, id: string | null | undefined): string => {
  if (!id) return 'the leader';
  for (const s of [frame.result.state, frame.start]) {
    const c = [...s.grid, ...s.retired].find((x) => x.card.driverId === id);
    if (c) return c.card.name;
  }
  return id;
};

const listNames = (names: string[]): string =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

export function afterLine(frame: RaceFrame, t: number, moment: TableMoment): string {
  const turn = frame.result.turns[t - 1];
  const ref = moment.ref;
  if (!turn) return '';
  switch (ref.kind) {
    case 'lead-challenge': {
      const ch = turn.events.filter(isSeq)[ref.seq]?.challenges[ref.k];
      const C = nameIn(frame, ref.challengerId);
      const L = nameIn(frame, ref.leaderId);
      if (!ch) return '';
      if (ch.voided && ch.momentTag !== 'challenge.no-defender') return `Triples — ${C}'s car breaks instead. ${L} stays in front.`;
      if (ch.success) {
        const how = ch.chip === 'challenger-spent' ? ', on a performance chip' : ch.grantUsed ? ', on his interview bonus' : '';
        return `${C} takes the lead${how}${ref.tieWin ? ` — ${ref.tieWin}` : ''}.`;
      }
      if (ch.chip === 'defender-spent') return `${C} had him — but ${L} burns a chip and keeps the lead.`;
      return ch.gasOk ? `${L} shuts the door${ref.tieHold ? ` — ${ref.tieHold}` : ''}.` : `${C} never gets the run. ${L} keeps the lead${ref.tieHold ? ` — ${ref.tieHold}` : ''}.`;
    }
    case 'top-challenge': {
      const ch = turn.events.filter(isSeq)[ref.seq]?.challenges[ref.k];
      const C = nameIn(frame, ref.challengerId);
      if (!ch) return '';
      if (ch.momentTag === 'challenge.no-defender') return `Nobody there — the white die found an empty slot. ${C} stays put.`;
      if (ch.voided) return `Triples — ${C}'s car breaks instead.`;
      const D = nameIn(frame, ch.defenderId);
      const to = ch.slot?.position ?? 0;
      if (ch.success) {
        const how = ch.chip === 'challenger-spent' ? ' on a performance chip' : ch.grantUsed ? ' on his interview bonus' : '';
        return `${C} is by ${D}${how} — up to ${ORD(to)}${ref.tieWin ? `, ${ref.tieWin}` : ''}!`;
      }
      if (ch.chip === 'defender-spent') return `${C} had him — but ${D} burns a chip and keeps ${ORD(to)}.`;
      return ch.gasOk ? `Held! ${D} slams the door on ${C}.` : `${C} never gets the run on ${D}.`;
    }
    case 'pit-lead': {
      const lc = turn.pit?.leadChallenge;
      const C = nameIn(frame, ref.challengerId);
      const L = nameIn(frame, ref.leaderId);
      if (!lc) return '';
      if (lc.momentTag === 'pit.lead-change') return `${C} beats ${L} out of the pits and has the lead${ref.tieWin ? ` — ${ref.tieWin}` : ''}.`;
      if (lc.momentTag === 'pit.lead-held-chip') return `${C} had him, but ${L} burns a chip and keeps the lead.`;
      return `${L} gets out first. ${C} comes out behind him.`;
    }
    case 'trouble':
    case 'rare': {
      const ev = turn.events.find((e) => e.eventType === (ref.kind === 'rare' ? 'rare' : 'trouble')) as (TroubleResult & { trouble?: TroubleResult | null }) | undefined;
      if (!ev) return '';
      if (ref.kind === 'rare') {
        const rare = ev as unknown as { entryText?: string; actorIds: string[] };
        const who = rare.actorIds.slice(0, 2).map((id) => nameIn(frame, id));
        const text = plainLine(rare.entryText ?? ev.description).replace(/\s+/g, ' ').trim();
        return `The sheet says: “${text}”${who.length ? ` — ${listNames(who)}.` : ''}`;
      }
      const collected = ev.collectedIds ?? [];
      if (collected.length === 0) return `Through it clean — nobody in the ${GROUP_NAME[ref.group]} group is collected.`;
      const leaderId = boardBefore(frame, t).grid.find((c) => c.position === 1)?.card.driverId;
      const names = collected.slice(0, 3).map((id) => nameIn(frame, id));
      const more = collected.length > 3 ? ` and ${collected.length - 3} more` : '';
      const out = (ev.outIds ?? []).map((id) => nameIn(frame, id));
      const parts = [`${collected.length} collected: ${listNames(names)}${more}.`];
      if (leaderId && collected.includes(leaderId)) parts.push('The leader is in it.');
      if (out.length) parts.push(`${listNames(out.slice(0, 2))} ${out.length === 1 ? 'is' : 'are'} out of the race.`);
      if (ev.bigOneTriggered) parts.push("It's the Big One.");
      return parts.join(' ');
    }
    case 'rain': {
      if (turn.called?.reason === 'rain') return `Rain. It's over — ${nameIn(frame, ref.leaderId)} wins it under the clouds.`;
      return 'The rain holds off. They race on.';
    }
    case 'photo-finish': {
      const a: PfaAttempt | undefined = frame.result.photoFinish?.attempts[ref.attempt];
      if (!a) return '';
      const A = nameIn(frame, a.attackerId);
      const D = nameIn(frame, a.defenderId);
      if (a.outcome === 'passed') return a.wonTheRace ? `${A} gets him at the stripe and wins it.` : `${A} nips ${D} on the line for ${ORD(a.toPosition)}.`;
      if (a.outcome === 'defended') return `${D} holds him off by inches.`;
      return `${A} can't get alongside. ${D} keeps it.`;
    }
  }
}

// ---------------------------------------------------------------------------
// Whole-race walk, for the measurement and the tests
// ---------------------------------------------------------------------------

export interface RaceStops {
  stops: number;
  byWhy: Record<string, number>;
  plans: TurnPlan[];
}

/**
 * Every pivotal throw the commissioner pace would stop for in this race, walked
 * turn by turn the way the shell plays it (`tabled` says whether the turn-level
 * gate opened the table for turn t).
 */
export function walkStops(frame: RaceFrame, tabled: (t: number) => boolean, ctx?: StakesContext): RaceStops {
  const byWhy: Record<string, number> = {};
  const plans: TurnPlan[] = [];
  let stops = 0;
  for (let t = 1; t <= frame.result.turnsRun; t++) {
    if (!tabled(t)) continue;
    const plan = planTurn(frame, t, { ctx, stopsSoFar: stops });
    stops += plan.moments.length;
    for (const m of plan.moments) byWhy[m.why] = (byWhy[m.why] ?? 0) + 1;
    plans.push(plan);
  }
  return { stops, byWhy, plans };
}

/**
 * THE COMMISSIONER'S GATE over a whole race — which turns the table opens for,
 * walked exactly as the shell walks it (`planCommissionerStep` from what is on
 * screen, the race cam call revealed first). Each decision reads only the board
 * before the turn and that turn's revealed call; computing them all at once is
 * the same as computing them one at a time (bigMoments.test.ts proves the gate
 * never reads ahead).
 */
export function commissionerGate(frame: RaceFrame): Set<number> {
  const set = new Set<number>();
  const r = frame.result;
  let peek: Peek | null = null;
  let cursor = 0;
  while (cursor < r.turnsRun) {
    const step = planCommissionerStep(visibleTurnState(raceViewAt(r, frame.start, cursor), r.scheduledTurns), peek);
    if (step.kind === 'peek') {
      peek = revealCamCall(r.turns[step.target - 1]);
      continue;
    }
    if (step.kind === 'table') set.add(step.target);
    cursor = step.target;
  }
  return set;
}

/** Everything the shell needs to play a race at "You roll the big ones". */
export interface CommissionerRace {
  /** Turns the table opens for. */
  gate: Set<number>;
  /** The planned stops of each opened turn (an opened turn may have none). */
  plans: Map<number, TurnPlan>;
  /** Commissioner throws in the whole race. */
  stops: number;
}

/**
 * Plan a whole race for the commissioner. Every decision in it is made from
 * what the table had shown before that throw (see `planTurn`); planning them
 * up front only saves recomputing them on every render.
 */
export function commissionerRace(frame: RaceFrame, ctx?: StakesContext): CommissionerRace {
  const gate = commissionerGate(frame);
  const walked = walkStops(frame, (t) => gate.has(t), ctx);
  return { gate, plans: new Map(walked.plans.map((p) => [p.turn, p])), stops: walked.stops };
}

/** Plain text of a card, for tests, reports and the screen reader summary. */
export function cardText(card: StakesCard): string {
  return [
    card.when,
    card.title,
    ...(card.headline ? [`${card.headline.text}: ${card.headline.odds}`] : []),
    ...card.needs.map((n) => (n.odds ? `${n.text} [${n.odds}]` : n.text)),
    ...card.rides,
  ].join('\n');
}

export type { Chance };
